import React from "react";

import { identity } from "~/tools/identity";
import { ImmutableSet } from "~/tools/immutable-set";
import { isDeepEqual } from "~/tools/is-deep-equal";
import { useHandler } from "~/tools/use-handler";

import { useErrorMessage } from "../components/_notification-error";
import { type Setter, resolve } from "../generic/setter";
import { mutateUserProfile, queryUserProfile } from "../queries";
import {
	type OptimisticUpdate,
	type UseMutationOptions,
	optimisticQueryUpdate,
	useMutation,
	useQuery,
	useQueryClient,
} from "../queries/react-query";

import type { UserProfile as UserProfileOpenAPI } from "./openapi";

export interface UserProfile
	extends Omit<UserProfileOpenAPI, "systemNotificationsRead" | "starredViews" | "starredDashboards"> {
	systemNotificationsRead: ImmutableSet<string>;
	starredViews: ImmutableSet<number>;
	starredDashboards: ImmutableSet<number>;
}

const userProfileFromOpenApi = (userProfile: UserProfileOpenAPI): UserProfile => ({
	...userProfile,
	systemNotificationsRead: ImmutableSet.fromArray(userProfile.systemNotificationsRead),
	starredViews: ImmutableSet.fromArray(userProfile.starredViews),
	starredDashboards: ImmutableSet.fromArray(userProfile.starredDashboards),
});

const userProfileToOpenApi = (userProfile: UserProfile): UserProfileOpenAPI => ({
	...userProfile,
	systemNotificationsRead: userProfile.systemNotificationsRead.toArray(),
	starredViews: userProfile.starredViews.toArray(),
	starredDashboards: userProfile.starredDashboards.toArray(),
});

export type UseUserProfileOptions = UseMutationOptions<
	UserProfileOpenAPI,
	Error,
	UserProfileOpenAPI,
	{ profile: undefined | OptimisticUpdate<UserProfileOpenAPI> }
>;

export interface UseUserProfileOptionsWithSelect<TData> extends UseUserProfileOptions {
	select: (userProfile: UserProfile) => TData;
}

export type UseUserProfileResult<TData> = [
	userProfile: undefined | TData,
	updateUserProfile: React.Dispatch<Setter<UserProfile>>,
];

export function useUserProfile<TData = UserProfile>({
	onMutate,
	onSuccess,
	onError,
	select = identity as (userProfile: UserProfile) => TData,
	...options
}: Partial<UseUserProfileOptionsWithSelect<TData>> = {}): UseUserProfileResult<TData> {
	const queryClient = useQueryClient();
	const { displayErrorMessage } = useErrorMessage();

	const { data: userProfile } = useQuery({
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		...queryUserProfile,
		select: userProfileFromOpenApi,
	});

	const { mutate: update } = useMutation({
		...mutateUserProfile,
		...options,
		onMutate: async (newUserProfile) => {
			const profile = optimisticQueryUpdate(queryClient, queryUserProfile, newUserProfile);

			await Promise.all([profile?.cancelOngoingQuery(), onMutate?.(newUserProfile)]);

			return { profile };
		},
		onSuccess: (data, variables, context) => {
			queryUserProfile.setData(queryClient, data);

			return onSuccess?.(data, variables, context);
		},
		onError: (error, variables, context) => {
			context?.profile?.rollbackOptimisticUpdates();

			if (onError != null) {
				return onError(error, variables, context);
			}

			displayErrorMessage({
				message: "Something went wrong while trying to save user profile",
				error,
			});
		},
	});

	const updateUserProfile = useHandler((setter: Setter<UserProfile>) => {
		const nextUserProfile = resolve(setter, userProfile);

		if (!isDeepEqual(userProfile, nextUserProfile)) {
			update(userProfileToOpenApi(nextUserProfile));
		}
	});

	const selected = React.useMemo(() => (userProfile == null ? undefined : select(userProfile)), [userProfile, select]);

	return [selected, updateUserProfile];
}
